/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../../../core/config/api';

/**
 * Gestionnaire générique d'un catalogue de configuration (CM).
 * Piloté par une description de champs, il sert aussi bien le catalogue de bonus
 * que celui des succès — même CRUD, mêmes garde-fous, une seule implémentation.
 */

export type CatalogueFieldType = 'text' | 'textarea' | 'number' | 'bool' | 'select';

export interface CatalogueField {
  key: string;
  label: string;
  type: CatalogueFieldType;
  options?: string[];
  required?: boolean;
  placeholder?: string;
  /** Valeur initiale d'un nouvel enregistrement. */
  defaultValue?: string | number | boolean;
}

export interface CatalogueColumn {
  key: string;
  label: string;
  /** Rendu personnalisé (badges, compteurs…). */
  render?: (row: any) => React.ReactNode;
}

interface Props {
  /** Segment d'URL sous /api/config (ex. 'bonus', 'succes'). */
  endpoint: string;
  title: string;
  description?: string;
  fields: CatalogueField[];
  columns: CatalogueColumn[];
  emptyLabel?: string;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

function blankDraft(fields: CatalogueField[]): Record<string, any> {
  const d: Record<string, any> = {};
  for (const f of fields) {
    d[f.key] =
      f.defaultValue !== undefined
        ? f.defaultValue
        : f.type === 'bool'
          ? true
          : f.type === 'number'
            ? 0
            : f.type === 'select'
              ? f.options?.[0] ?? ''
              : '';
  }
  return d;
}

const CatalogueManager: React.FC<Props> = ({
  endpoint,
  title,
  description,
  fields,
  columns,
  emptyLabel = 'Aucune entrée. Créez-en une pour commencer.',
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, any> | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const base = useMemo(() => `${getApiBaseUrl()}/api/config/${endpoint}`, [endpoint]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(base, { headers: authHeaders(), credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur de chargement');
      setRows(Array.isArray(data.data) ? data.data : []);
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  const startNew = () => {
    setEditingId(null);
    setDraft(blankDraft(fields));
    setNotice(null);
    setError(null);
  };

  const startEdit = (row: any) => {
    const d: Record<string, any> = {};
    for (const f of fields) d[f.key] = row[f.key] ?? blankDraft(fields)[f.key];
    setEditingId(Number(row.id));
    setDraft(d);
    setNotice(null);
    setError(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(editingId ? `${base}/${editingId}` : base, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(),
        credentials: 'include',
        body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur d’enregistrement');
      setNotice(data.message || 'Enregistré');
      setDraft(null);
      setEditingId(null);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    setDeletingId(id);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${base}/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
        credentials: 'include',
      });
      const data = await res.json();
      // 409 = refus volontaire côté serveur (ex. succès déjà débloqué) : on affiche le motif.
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur de suppression');
      setNotice(data.message || 'Supprimé');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Erreur réseau');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/75 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-white">{title}</h2>
          {description ? <p className="mt-0.5 text-[11px] text-slate-400">{description}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg border border-white/15 bg-black/30 px-3 py-1.5 text-xs text-slate-200 hover:bg-black/45"
          >
            Rafraîchir
          </button>
          <button
            type="button"
            onClick={startNew}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
          >
            ＋ Nouveau
          </button>
        </div>
      </div>

      {error ? (
        <p className="mb-2 rounded border border-red-500/40 bg-red-950/30 px-2 py-1.5 text-xs text-red-300">{error}</p>
      ) : null}
      {notice ? (
        <p className="mb-2 rounded border border-emerald-500/40 bg-emerald-950/30 px-2 py-1.5 text-xs text-emerald-300">
          {notice}
        </p>
      ) : null}

      {draft ? (
        <div className="mb-3 rounded-lg border border-[#33456f] bg-[#101f44] p-3">
          <p className="mb-2 text-xs font-semibold text-white">
            {editingId ? 'Modifier l’entrée' : 'Nouvelle entrée'}
          </p>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
            {fields.map((f) => (
              <label key={f.key} className="flex flex-col gap-1 text-[11px] text-slate-300">
                <span>
                  {f.label}
                  {f.required ? <span className="text-red-400"> *</span> : null}
                </span>
                {f.type === 'bool' ? (
                  <select
                    value={draft[f.key] ? '1' : '0'}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value === '1' })}
                    className="rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100"
                  >
                    <option value="1">Oui</option>
                    <option value="0">Non</option>
                  </select>
                ) : f.type === 'select' ? (
                  <select
                    value={String(draft[f.key] ?? '')}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    className="rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100"
                  >
                    {(f.options || []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : f.type === 'textarea' ? (
                  <textarea
                    value={String(draft[f.key] ?? '')}
                    onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    rows={2}
                    className="rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100"
                  />
                ) : (
                  <input
                    type={f.type === 'number' ? 'number' : 'text'}
                    value={String(draft[f.key] ?? '')}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value,
                      })
                    }
                    placeholder={f.placeholder}
                    className="rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100"
                  />
                )}
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void save()}
              className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraft(null);
                setEditingId(null);
              }}
              className="rounded bg-zinc-700 px-3 py-1.5 text-xs text-white hover:bg-zinc-600"
            >
              Annuler
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-left">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className="border-b border-[#1e2d4d]/80 bg-[#0d152b]/60 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500"
                >
                  {c.label}
                </th>
              ))}
              <th className="border-b border-[#1e2d4d]/80 bg-[#0d152b]/60 px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-2 py-6 text-center text-xs text-slate-500">
                  Chargement…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="px-2 py-6 text-center text-xs text-slate-500">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  {columns.map((c) => (
                    <td key={c.key} className="border-b border-[#1e2d4d]/60 px-2 py-2 text-[11px] text-slate-200">
                      {c.render ? c.render(row) : String(row[c.key] ?? '—')}
                    </td>
                  ))}
                  <td className="border-b border-[#1e2d4d]/60 px-2 py-2 text-[11px]">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(row)}
                        className="rounded border border-slate-600/50 bg-slate-800/50 px-2 py-0.5 text-[10px] text-slate-200 hover:bg-slate-700/70"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        disabled={deletingId === Number(row.id)}
                        onClick={() => void remove(Number(row.id))}
                        className="rounded border border-red-500/40 bg-red-900/30 px-2 py-0.5 text-[10px] text-red-300 hover:bg-red-800/50 disabled:opacity-50"
                      >
                        {deletingId === Number(row.id) ? '…' : 'Supprimer'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CatalogueManager;
